import React, {useState} from 'react';
import useNotification from '../../../utility/hooks/useNotification';
import {Box, Button, Divider, Stack, Typography} from '@mui/material';
import {DateTimePicker} from '@mui/x-date-pickers';
import moment from 'moment/moment';
import PropTypes from 'prop-types';
import CancelIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import {useConfirm} from 'material-ui-confirm';

export const GoLive = ({
                           formId,
                           formType,
                           hasRecipients,
                           onMakeLive,
                           onSchedule,
                           onLater,
                           formName,
                           expirationDate,
                           scheduledActivationAt,
                           onScheduledActivationChange,
                       }) => {
    const [date, setDate] = useState(
        scheduledActivationAt ? moment(scheduledActivationAt) : null
    );
    const [dateError, setDateError] = useState('');

    const isPublicForm =
        formType === 'shareable' ||
        formType === 'application' ||
        formType === 'contact';

    const hasExpiration = !!expirationDate;
    const expirationMoment = hasExpiration ? moment(expirationDate) : null;
    const confirm = useConfirm();
    const {showErrorNotification} = useNotification();

    const handleDateChange = (newDate) => {
        setDate(newDate);

        if (onScheduledActivationChange) {
            onScheduledActivationChange(
                newDate ? newDate.toDate().toISOString() : null
            );
        }

        if (!newDate || !hasExpiration) {
            setDateError('');
            return;
        }

        if (newDate.isAfter(expirationMoment)) {
            setDateError('Activation date must be on or before the expiration date.');
        } else {
            setDateError('');
        }
    };

    const handleMakeLiveNow = () => {
        if (hasExpiration && moment().isAfter(expirationMoment)) {
            showErrorNotification(
                'This form has already reached its expiration date and cannot be made live.'
            );
            return;
        }

        onMakeLive(formId, formName);
    };

    const handleMakeFormLiveDialog = async () => {

        try {
            await confirm({
                title: 'Make Form Live',
                description: (
                    <>
                        Are you sure you want to make this form live? Email notifications will be sent to all assigned
                        recipients, and this action cannot be undone.
                        <br/><br/>
                        <strong>{formName}</strong>
                    </>
                ),
                confirmationText: 'Confirm',
                cancellationText: 'Cancel',
            });
            await handleMakeLiveNow();

        } catch {
        }
    };

    const handleScheduleClick = () => {
        if (!date) return;

        if (dateError) {
            showErrorNotification(dateError);
            return;
        }

        onSchedule(formId, date.toDate().toISOString());
    };

    const handleClearDate = () => {
        setDate(null);
        setDateError('');

        if (onScheduledActivationChange) {
            onScheduledActivationChange(null);
        }
    };

    return (
        <Box sx={{p: 3}}>
            <Typography fontWeight="600" variant="body1" color="text.primary" mb={3}>
                When would you like this form to go live?
            </Typography>

            {(!hasRecipients && !isPublicForm && formType !== 'triggerForm') && (
                <Typography variant="body2" color="error" mb={2}>
                    This form has no recipients. You cannot make it live until at least one
                    recipient is added. You can still schedule a future live date.
                </Typography>
            )}

            <Stack spacing={2}>
                <Stack
                    direction={{
                        xs: 'column',
                        sm: 'row',
                    }}
                    spacing={1.5}
                >
                    <Button
                        variant="contained"
                        disabled={
                            (!hasRecipients && !isPublicForm && formType !== 'triggerForm') ||
                            (hasExpiration && moment().isAfter(expirationMoment))
                        }
                        onClick={handleMakeFormLiveDialog}
                        sx={{
                            backgroundColor: '#186BBE',
                            boxShadow: 'none',
                            width: {
                                xs: '100%',
                                sm: '250px',
                            },
                            '&:hover': {
                                backgroundColor: '#155FA7',
                                boxShadow: 'none',
                            },
                        }}
                    >
                        Make Live Now
                    </Button>

                    <Button
                        variant="outlined"
                        onClick={onLater}
                        sx={{
                            width: {
                                xs: '100%',
                                sm: '250px',
                            },
                        }}
                    >
                        Make Live Later
                    </Button>
                </Stack>

                <Box>
                    <Divider sx={{my: 2}}/>
                </Box>

                <Box
                    sx={{
                        p: 2,
                        border: '1px solid #ddd',
                        borderRadius: 2,
                    }}
                >
                    {hasExpiration && (
                        <Typography variant="body2" color="text.secondary" mb={1}>
                            This form expires on {moment(expirationDate).format('MMM D, YYYY')}.
                            The activation date must be on or before this.
                        </Typography>
                    )}

                    <Typography variant="subtitle1" mb={1}>
                        Schedule a future live date
                    </Typography>

                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Box sx={{flexGrow: 1}}>
                            <DateTimePicker
                                label="Activation date"
                                format="MMM D, YYYY, HH:mm"
                                value={date}
                                onChange={handleDateChange}
                                minDateTime={moment()}
                                maxDateTime={hasExpiration ? expirationMoment : undefined}
                                ampm={false}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        helperText: dateError
                                            ? dateError
                                            : 'Form activates at the selected time when it becomes live.',
                                        inputProps: {readOnly: true},
                                    },
                                    field: {readOnly: true},
                                }}
                            />
                        </Box>

                        {date && (
                            <IconButton
                                aria-label="Clear activation date"
                                onClick={handleClearDate}
                                size="small"
                                sx={{
                                    transform: 'translateY(-10px)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <CancelIcon fontSize="small"/>
                            </IconButton>
                        )}
                    </Stack>

                    <Button
                        variant="outlined"
                        disabled={!date || !!dateError}
                        onClick={handleScheduleClick}
                        sx={{
                            mt: 2,
                            width: {
                                xs: '100%',
                                sm: '250px',
                            },
                        }}
                    >
                        Schedule Live Date
                    </Button>
                </Box>
            </Stack>
        </Box>
    );
};

GoLive.propTypes = {
    formId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    formType: PropTypes.string.isRequired,
    hasRecipients: PropTypes.bool.isRequired,
    onMakeLive: PropTypes.func.isRequired,
    onSchedule: PropTypes.func.isRequired,
    onLater: PropTypes.func.isRequired,
    formName: PropTypes.string,
    expirationDate: PropTypes.string,
    scheduledActivationAt: PropTypes.string,
    onScheduledActivationChange: PropTypes.func,
};
